import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { makeStyles } from 'tss-react/mui';

import {
    Avatar,
    Box,
    Button,
    FormControl,
    FormControlLabel,
    InputLabel,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
    SvgIcon,
    Switch,
    Typography
} from '@mui/material';

import IconClose from 'app/ui-kit/icons/Close.svg';
import IconDown from 'app/ui-kit/icons/KeyboardArrowDown.svg';
import useI18n from 'hooks/use-i18n';
import useStores from 'hooks/use-stores';
import Utils from 'utils/Utils';

const useStyles = makeStyles()({
    root: {
        marginTop: '16px',
        padding: 20,
        background: '#DFDDD933',
        borderRadius: 24,
        border: '1px solid rgba(223, 221, 217, 0.4)',
        '@media (max-width: 480px)': {
            flexDirection: 'column'
        }
    },
    fields: {
        display: 'flex',
        justifyContent: 'space-around',
        flexWrap: 'wrap'
    },
    label: {
        fontSize: 14
    },
    button: {
        marginLeft: 8
    },
    field: {
        '& .MuiInput-root::before, .MuiInput-root::after, .MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottom: 'none'
        },
        '& .MuiSelect-select': {
            paddingLeft: 8,
            fontSize: 12
        },
        '& .MuiSelect-select:focus': {
            borderRadius: 8
        },
        minWidth: 200,
        fontSize: 12,
        padding: '0 8px 12px',
        marginBottom: 8,
        borderRadius: 18,
        background: '#fff',
        '@media (max-width: 480px)': {
            marginBottom: '8px',
            maxWidth: 'initial',
            flex: 1
        }
    },
    select: {
        '& .MuiSelect-root': {
            display: 'flex',
            flexDirection: 'row'
        },
        '& .MuiSelect-root .MuiTypography-root': {
            fontSize: 14
        }
    },
    switch: {
        padding: '16px 16px 2px 0',
        marginLeft: 0,
        color: '#979797',
        '& .MuiFormControlLabel-label': {
            flex: 1,
            color: '#979797'
        }
    },
    avatar: {
        width: 32,
        height: 32
    },
    capitalize: {
        textTransform: 'capitalize'
    }
});

const SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const FAMILIES = [
    'unix',
    'blog',
    'software',
    'info',
    'exploit',
    'NVD',
    'bugbounty',
    'tools',
    'microsoft',
    'scanner',
    'ioc'
];

const SearchBarSettings = () => {
    const { classes } = useStyles();
    const history = useHistory();
    const i18n = useI18n();
    const { statsStore, searchStore } = useStores();

    const [type, setType] = useState('');
    const [order, setOrder] = useState('');
    const [date, setDate] = useState('');
    const [bulletinFamily, setBulletinFamily] = useState('');
    const [ioc, setIoc] = useState(false);
    const [score, setScore] = useState('');
    const [query, setQuery] = useState('');

    const queryRef = useRef(query);
    queryRef.current = query;
    const iocRef = useRef(ioc);
    iocRef.current = ioc;

    useEffect(() => {
        const state = { type, order, bulletinFamily };
        let query = Object.keys(state)
            .filter((i) => !!state[i])
            .map((i) => (i ? i + ':' : '') + state[i])
            .join(' AND ');

        if (score) {
            query = query + ` cvss.score:[${score} TO 10]`;
        }
        if (date) {
            query = query + ' ' + date;
        }

        setQuery(query);
    }, [type, order, date, bulletinFamily, score]);

    const handleClear = useCallback(() => {
        setType('');
        setOrder('');
        setDate('');
        setBulletinFamily('');
        setIoc(false);
        setScore(null);
        searchStore.doSearch();
    }, [searchStore]);

    const handleSearch = useCallback(() => {
        const search = [iocRef.current && 'include_ioc=true', queryRef.current && `query=${queryRef.current}`]
            .filter(Boolean)
            .join('&');
        history.push({ pathname: '/search', search });
        searchStore.doSearch({ query: queryRef.current });
    }, [history, searchStore]);

    const suggestMenu = useMemo(
        () =>
            Object.keys(statsStore.types || {})
                .sort()
                .map((k, i) => (
                    <MenuItem key={i} value={k} style={{ display: 'flex', flexDirection: 'row' }}>
                        <ListItemIcon>
                            <Avatar className={classes.avatar} alt={k} src={Utils.getImage(k)} />
                        </ListItemIcon>
                        <ListItemText>{k}</ListItemText>
                    </MenuItem>
                )),
        [classes.avatar, statsStore.types]
    );

    const familyMenu = useMemo(
        () =>
            FAMILIES.map((family) => (
                <MenuItem key={'family-' + family} value={family}>
                    <Typography className={classes.capitalize} variant="inherit">
                        {family}
                    </Typography>
                </MenuItem>
            )),
        [classes.capitalize]
    );

    const handleBulletinFamilyChange = useCallback(({ target }) => setBulletinFamily(target.value), []);
    const handleTypeChange = useCallback(({ target }) => setType(target.value), []);
    const handleScoreChange = useCallback(({ target }) => setScore(target.value), []);
    const handleDateChange = useCallback(({ target }) => setDate(target.value), []);
    const handleOrderChange = useCallback(({ target }) => setOrder(target.value), []);
    const handleIocChange = useCallback(({ target }) => setIoc(target.checked), []);

    return (
        <Box className={classes.root}>
            <Box className={classes.fields}>
                <Box className={classes.field}>
                    <FormControl fullWidth>
                        <InputLabel className={classes.label}>Family</InputLabel>
                        <Select
                            fullWidth
                            displayEmpty
                            value={bulletinFamily}
                            IconComponent={IconDown}
                            onChange={handleBulletinFamilyChange}
                        >
                            {familyMenu}
                        </Select>
                    </FormControl>
                </Box>

                <Box className={classes.field}>
                    <FormControl fullWidth>
                        <InputLabel className={classes.label}>{i18n('bulletinType')}</InputLabel>
                        <Select
                            fullWidth
                            displayEmpty
                            IconComponent={IconDown}
                            value={type}
                            className={classes.select}
                            onChange={handleTypeChange}
                        >
                            {suggestMenu}
                        </Select>
                    </FormControl>
                </Box>

                <Box className={classes.field}>
                    <FormControl fullWidth>
                        <InputLabel className={classes.label}>Min CVSS Score</InputLabel>
                        <Select
                            fullWidth
                            displayEmpty
                            value={score}
                            IconComponent={IconDown}
                            label={i18n('date')}
                            onChange={handleScoreChange}
                        >
                            {SCORES.map((score) => (
                                <MenuItem key={'filter-date-' + score} value={score}>
                                    {score}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box className={classes.field}>
                    <FormControl fullWidth>
                        <InputLabel className={classes.label}>Date</InputLabel>
                        <Select
                            fullWidth
                            displayEmpty
                            value={date}
                            IconComponent={IconDown}
                            label={i18n('date')}
                            onChange={handleDateChange}
                        >
                            <MenuItem value="last 10 days">Last 10 Days</MenuItem>
                            <MenuItem value="last month">Last Month</MenuItem>
                            <MenuItem value="last 6 month">Last 6 Month</MenuItem>
                            <MenuItem value="last year">Last Year</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box className={classes.field}>
                    <FormControl fullWidth>
                        <InputLabel className={classes.label}>Order by</InputLabel>
                        <Select
                            fullWidth
                            displayEmpty
                            value={order}
                            IconComponent={IconDown}
                            label={i18n('orderBy')}
                            onChange={handleOrderChange}
                        >
                            <MenuItem value="published">Date published</MenuItem>
                            <MenuItem value="cvss.score">CVSS Score</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box className={classes.field}>
                    <FormControl fullWidth>
                        <FormControlLabel
                            className={classes.switch}
                            label="Include Ioc"
                            labelPlacement="start"
                            control={<Switch size="small" color="primary" checked={ioc} onChange={handleIocChange} />}
                        />
                    </FormControl>
                </Box>
            </Box>

            <Box display="flex" justifyContent="flex-end">
                <Button
                    className={classes.button}
                    style={{ background: '#6161E1', color: '#fff' }}
                    onClick={handleSearch}
                    variant="outlined"
                >
                    Show Results
                </Button>
                <Button
                    className={classes.button}
                    variant="outlined"
                    onClick={handleClear}
                    endIcon={<SvgIcon component={IconClose} />}
                >
                    Clear
                </Button>
            </Box>
        </Box>
    );
};

export default observer(SearchBarSettings);
